const API_BASE_URL = '/api';

class PermissionService {
  /**
   * Check if user has a specific permission in a scope
   * @param {string} userId - User ID
   * @param {string} permission - Permission name (e.g., "BAIQL_Scripts_Create")
   * @param {string} scopeType - "Global", "Team", or "Organization"
   * @param {number|null} scopeId - Team ID or Org ID (null for Global)
   * @returns {Promise<boolean>}
   */
  async hasPermission(userId, permission, scopeType, scopeId) {
    try {
      // For now, we'll check permissions by calling the backend
      // In a real implementation, you might cache permissions client-side
      const response = await fetch(`${API_BASE_URL}/permissions/check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          userId,
          permission,
          scopeType,
          scopeId
        })
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      return data.hasPermission === true;
    } catch (error) {
      console.error('Error checking permission:', error);
      return false;
    }
  }

  /**
   * Get user's roles in a specific scope
   * @param {string} userId - User ID
   * @param {string} scopeType - "Global", "Team", or "Organization"
   * @param {number|null} scopeId - Team ID or Org ID
   * @returns {Promise<Array>}
   */
  async getUserRoles(userId, scopeType, scopeId) {
    try {
      const response = await fetch(`${API_BASE_URL}/role-assignments/user/${userId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        return { subscriptionRoles: [], functionalRoles: [] };
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting user roles:', error);
      return { subscriptionRoles: [], functionalRoles: [] };
    }
  }

  /**
   * Get all effective permissions for a user in a scope
   * @param {string} userId - User ID
   * @param {string} scopeType - "Global", "Team", or "Organization"
   * @param {number|null} scopeId - Team ID or Org ID
   * @returns {Promise<Object>}
   */
  async getEffectivePermissions(userId, scopeType, scopeId) {
    try {
      const response = await fetch(`${API_BASE_URL}/permissions/effective`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          userId,
          scopeType,
          scopeId
        })
      });

      if (!response.ok) {
        return {};
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting effective permissions:', error);
      return {};
    }
  }

  /**
   * Check if user is a PaidUser
   * @param {string} userId - User ID
   * @returns {Promise<boolean>}
   */
  async isPaidUser(userId) {
    try {
      const roles = await this.getUserRoles(userId, 'Global', null);
      return roles.subscriptionRoles?.includes('PaidUser') || false;
    } catch (error) {
      console.error('Error checking PaidUser status:', error);
      return false;
    }
  }

  /**
   * Check if user is a FreeUser
   * @param {string} userId - User ID
   * @returns {Promise<boolean>}
   */
  async isFreeUser(userId) {
    try {
      const roles = await this.getUserRoles(userId, 'Global', null);
      return roles.subscriptionRoles?.includes('FreeUser') || false;
    } catch (error) {
      console.error('Error checking FreeUser status:', error);
      return false;
    }
  }

  /**
   * Get all roles available for a scope
   * @param {string} scopeType - "Global", "Team", or "Organization"
   * @param {number|null} scopeId - Team ID or Org ID
   * @returns {Promise<Array>}
   */
  async getRoles(scopeType, scopeId) {
    try {
      const params = new URLSearchParams({ scopeType });
      if (scopeId !== null && scopeId !== undefined) {
        params.append('scopeId', scopeId);
      }

      const response = await fetch(`${API_BASE_URL}/roles?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        return [];
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting roles:', error);
      return [];
    }
  }

  /**
   * Get default system roles
   * @returns {Promise<Array>}
   */
  async getDefaultRoles() {
    try {
      const response = await fetch(`${API_BASE_URL}/roles/defaults`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        return [];
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting default roles:', error);
      return [];
    }
  }

  /**
   * Get public/shared custom roles
   * @returns {Promise<Array>}
   */
  async getPublicRoles() {
    try {
      const response = await fetch(`${API_BASE_URL}/roles/public`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        return [];
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting public roles:', error);
      return [];
    }
  }

  /**
   * Get permission matrix
   * @returns {Promise<Object>}
   */
  async getPermissionMatrix() {
    try {
      const response = await fetch(`${API_BASE_URL}/roles/permission-matrix`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        return { roles: [], permissions: [] };
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting permission matrix:', error);
      return { roles: [], permissions: [] };
    }
  }

  /**
   * Assign role to user
   * @param {string} targetUserId - User ID to assign role to
   * @param {number} roleId - Role ID
   * @param {string} scopeType - "Global", "Team", or "Organization"
   * @param {number|null} scopeId - Team ID or Org ID
   * @returns {Promise<Object>}
   */
  async assignRole(targetUserId, roleId, scopeType, scopeId) {
    try {
      const response = await fetch(`${API_BASE_URL}/role-assignments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          targetUserId,
          roleId,
          scopeType,
          scopeId
        })
      });

      if (!response.ok) {
        let errorMessage = 'Failed to assign role';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const error = await response.json();
            errorMessage = error.message || errorMessage;
          } else {
            const text = await response.text();
            errorMessage = text || errorMessage;
          }
        } catch (e) {
          // If parsing fails, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }
      return {};
    } catch (error) {
      console.error('Error assigning role:', error);
      throw error;
    }
  }

  /**
   * Remove role assignment
   * @param {number} assignmentId - Role assignment ID
   * @returns {Promise<void>}
   */
  async removeRoleAssignment(assignmentId) {
    try {
      const response = await fetch(`${API_BASE_URL}/role-assignments/${assignmentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to remove role assignment');
      }
    } catch (error) {
      console.error('Error removing role assignment:', error);
      throw error;
    }
  }

  /**
   * Get role assignments for a scope
   * @param {string} scopeType - "Global", "Team", or "Organization"
   * @param {number} scopeId - Team ID or Org ID
   * @returns {Promise<Array>}
   */
  async getScopeRoleAssignments(scopeType, scopeId) {
    try {
      const response = await fetch(`${API_BASE_URL}/role-assignments/scope/${scopeType}/${scopeId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        return [];
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting scope role assignments:', error);
      return [];
    }
  }

  /**
   * Get audit log
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>}
   */
  async getAuditLog(filters = {}) {
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key] !== null && filters[key] !== undefined) {
          params.append(key, filters[key]);
        }
      });

      const response = await fetch(`${API_BASE_URL}/permission-audit?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        return { items: [], totalCount: 0, page: 1, pageSize: 50, totalPages: 0 };
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting audit log:', error);
      return { items: [], totalCount: 0, page: 1, pageSize: 50, totalPages: 0 };
    }
  }

  /**
   * Get permissions grouped by category
   * @returns {Promise<Array>}
   */
  async getPermissionsByCategory() {
    try {
      const response = await fetch(`${API_BASE_URL}/permissions/categories`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        let errorMessage = 'Failed to load permissions';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const error = await response.json();
            errorMessage = error.message || errorMessage;
          } else {
            const text = await response.text();
            errorMessage = text || errorMessage;
          }
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(`${errorMessage} (Status: ${response.status})`);
      }

      const data = await response.json();
      console.log('Permissions loaded:', data);
      return data;
    } catch (error) {
      console.error('Error getting permissions by category:', error);
      throw error;
    }
  }

  /**
   * Create a custom role
   * @param {Object} roleData - Role creation data
   * @param {string} roleData.name - Role name (required)
   * @param {string} roleData.description - Role description (optional)
   * @param {string} roleData.scopeType - "Global", "Team", or "Organization"
   * @param {number|null} roleData.scopeId - Team ID or Org ID (null for public/shared)
   * @param {boolean} roleData.requiresPaidUser - Whether role requires paid user
   * @param {Array<number>} roleData.permissionIds - Array of permission IDs to assign
   * @returns {Promise<Object>}
   */
  async createRole(roleData) {
    try {
      const response = await fetch(`${API_BASE_URL}/roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          name: roleData.name,
          description: roleData.description || null,
          scopeType: roleData.scopeType || null,
          scopeId: roleData.scopeId || null,
          requiresPaidUser: roleData.requiresPaidUser || false,
          permissionIds: roleData.permissionIds || []
        })
      });

      if (!response.ok) {
        let errorMessage = 'Failed to create role';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const error = await response.json();
            errorMessage = error.message || errorMessage;
          } else {
            const text = await response.text();
            errorMessage = text || errorMessage;
          }
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        console.error('Create role API error:', response.status, errorMessage);
        throw new Error(`${errorMessage} (Status: ${response.status})`);
      }

      const result = await response.json();
      console.log('Create role API success:', result);
      return result;
    } catch (error) {
      console.error('Error creating role:', error);
      throw error;
    }
  }
}

const permissionServiceInstance = new PermissionService();
export default permissionServiceInstance;

